import React, { useState, useEffect } from 'react';
import { statsAPI, configAPI, apprenantsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../components/Icon';
import { colors } from '../theme/colors';
import DateRangePicker from '../components/DateRangePicker';

const Statistiques = () => {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [apprenants, setApprenants] = useState([]);
  const [pendingDateDebut, setPendingDateDebut] = useState('');
  const [pendingDateFin, setPendingDateFin] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    filiere: '',
    promotion: '',
    annee: '',
    dateDebut: '',
    dateFin: ''
  });
  const [loading, setLoading] = useState(true);

  const [allApprenants, setAllApprenants] = useState([]);

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    const fetchAllApprenants = async () => {
      try {
        const response = await apprenantsAPI.getAll({ limit: 1000 });
        setAllApprenants(response.data.apprenants || []);
      } catch (error) {
        console.error('Erreur chargement apprenants pour filtres:', error);
      }
    };
    fetchAllApprenants();
  }, []);

  const fetchData = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '')
      );

      const [statsResponse, configResponse, apprenantsResponse] = await Promise.all([
        statsAPI.get(params),
        configAPI.get(),
        apprenantsAPI.getAll({ limit: 1000, ...params })
      ]);
      setStats(statsResponse.data);
      setConfig(configResponse.data);
      setApprenants(apprenantsResponse.data.apprenants || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  // ─── Filtrage côté client (fallback si l'API ne filtre pas tout) ──────────
  const getFilteredApprenants = () => {
    return apprenants.filter(a => {
      // Filtre type
      if (filters.type && a.typeFormation !== filters.type) return false;

      // Filtre filière
      if (filters.filiere && a.filiere !== filters.filiere) return false;

      // Filtre promotion
      if (filters.promotion && a.promotion !== filters.promotion) return false;

      // Filtre année (via anneeFormation de l'apprenant)
      if (filters.annee && String(a.anneeFormation) !== String(filters.annee)) return false;

      // Filtre période précise (dateDebut / dateFin du filtre) — uniquement pour les apprenants ayant une dateDebut
      if (filters.dateDebut && a.dateDebut) {
        if (new Date(a.dateDebut) < new Date(filters.dateDebut)) return false;
      }
      if (filters.dateFin && a.dateDebut) {
        if (new Date(a.dateDebut) > new Date(filters.dateFin)) return false;
      }

      return true;
    });
  };

  const getByTypeFromFiltered = () => {
    if (!config) return {};
    const filtered = getFilteredApprenants();
    const positifs = config.statutsInsertion?.positif || [];
    const result = {};

    filtered.forEach(a => {
      const type = a.typeFormation || 'Non précisé';
      if (!result[type]) {
        result[type] = { total: 0, hommes: 0, femmes: 0, inseres: 0, positifs: 0 };
      }
      result[type].total++;
      if (a.genre === 'Homme') result[type].hommes++;
      if (a.genre === 'Femme') result[type].femmes++;
      if (a.dateInsertion) result[type].inseres++;
      if (positifs.includes(a.statutInsertion)) result[type].positifs++;
    });

    // Calculer les taux
    Object.keys(result).forEach(type => {
      const d = result[type];
      d.tauxFemmes = d.total > 0 ? ((d.femmes / d.total) * 100).toFixed(1) : 0;
      d.tauxInsertion = d.total > 0 ? ((d.inseres / d.total) * 100).toFixed(1) : 0;
      d.tauxPositif = d.total > 0 ? ((d.positifs / d.total) * 100).toFixed(1) : 0;
    });

    return result;
  };

  const handleFilterChange = (name, value) => {
    if (name === 'dateDebut' || name === 'dateFin') {
      setFilters(prev => ({
        ...prev,
        [name]: value,
        annee: ''
      }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleStartDateChange = (date) => {
    setPendingDateDebut(date);
  };

  const handleEndDateChange = (date) => {
    setPendingDateFin(date);
  };

  const applyPeriode = () => {
    setFilters(prev => ({
      ...prev,
      dateDebut: pendingDateDebut,
      dateFin: pendingDateFin,
      annee: ''
    }));
  };

  const resetPeriode = () => {
    setPendingDateDebut('');
    setPendingDateFin('');
    setFilters(prev => ({ ...prev, dateDebut: '', dateFin: '' }));
  };

  const resetFilters = () => {
    setPendingDateDebut('');
    setPendingDateFin('');
    setFilters({
      type: '',
      filiere: '',
      promotion: '',
      annee: '',
      dateDebut: '',
      dateFin: ''
    });
  };

  // ─── Fonctions graphiques — toutes utilisent getFilteredApprenants() ───────

  const getDiplomeData = () => {
    const filtered = getFilteredApprenants();
    const diplomes = {};
    filtered.forEach(a => {
      const diplome = a.diplome || 'Non précisé';
      diplomes[diplome] = (diplomes[diplome] || 0) + 1;
    });
    return Object.keys(diplomes).map(d => ({ name: d, value: diplomes[d] }));
  };

  const getStatutEntreeData = () => {
    const filtered = getFilteredApprenants();
    const statuts = {};
    filtered.forEach(a => {
      const statut = a.statutEntree || 'Non précisé';
      statuts[statut] = (statuts[statut] || 0) + 1;
    });
    return Object.keys(statuts).map(s => ({ name: s, value: statuts[s] }));
  };

  const getGenreData = () => {
    const filtered = getFilteredApprenants();
    const genres = { Homme: 0, Femme: 0 };
    filtered.forEach(a => {
      if (a.genre && genres.hasOwnProperty(a.genre)) {
        genres[a.genre]++;
      }
    });
    // N'afficher que les genres qui ont au moins 1 apprenant
    return Object.keys(genres)
      .filter(g => genres[g] > 0)
      .map(g => ({ name: g, value: genres[g] }));
  };

  const getSortieData = () => {
    if (!config) return [];
    const filtered = getFilteredApprenants();
    const positifs = config.statutsInsertion?.positif || [];
    const negatifs = config.statutsInsertion?.negatif || [];
    let positif = 0, negatif = 0, autre = 0;
    filtered.forEach(a => {
      if (positifs.includes(a.statutInsertion)) positif++;
      else if (negatifs.includes(a.statutInsertion)) negatif++;
      else autre++;
    });
    // Ne retourner que les catégories non nulles
    return [
      { name: 'Positif', value: positif },
      { name: 'Négatif', value: negatif },
      { name: 'Autre', value: autre }
    ].filter(item => item.value > 0);
  };

  const COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const byTypeFiltered = getByTypeFromFiltered();

  const filieres = [...new Set(allApprenants.map(a => a.filiere).filter(Boolean))];
  const promotions = [...new Set(allApprenants.map(a => a.promotion).filter(Boolean))];
  const annees = [...new Set(allApprenants.map(a => a.anneeFormation).filter(Boolean))].sort();

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
        <Icon name="statistiques" size={28} className="mr-3 text-red-600" />
        Statistiques
      </h2>

      {/* Filtres existants et Filtre période */}
      <div className="section-card bg-white rounded-xl p-4 mb-6">
        <div className="flex flex-col items-center gap-3 mb-4">
          <DateRangePicker
            startDate={pendingDateDebut}
            endDate={pendingDateFin}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onReset={resetPeriode}
          />
          <button
            onClick={applyPeriode}
            disabled={!pendingDateDebut || !pendingDateFin}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Appliquer la période
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          <div>
            <label htmlFor="stats-filter-type" className="block text-sm font-medium mb-1">Type</label>
            <select
              id="stats-filter-type"
              name="type"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              autoComplete="off"
            >
              <option value="">Tous</option>
              {config?.typesFormation?.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="stats-filter-filiere" className="block text-sm font-medium mb-1">Filière</label>
            <select
              id="stats-filter-filiere"
              name="filiere"
              value={filters.filiere}
              onChange={(e) => handleFilterChange('filiere', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              autoComplete="off"
            >
              <option value="">Toutes</option>
              {filieres.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="stats-filter-promotion" className="block text-sm font-medium mb-1">Promotion</label>
            <select
              id="stats-filter-promotion"
              name="promotion"
              value={filters.promotion}
              onChange={(e) => handleFilterChange('promotion', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              autoComplete="off"
            >
              <option value="">Toutes</option>
              {promotions.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="stats-filter-annee" className="block text-sm font-medium mb-1">Année</label>
            <select
              id="stats-filter-annee"
              name="annee"
              value={filters.annee}
              onChange={(e) => handleFilterChange('annee', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              autoComplete="off"
              disabled={filters.dateDebut && filters.dateFin}
            >
              <option value="">Toutes</option>
              {annees.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>

        {(filters.type || filters.filiere || filters.promotion || filters.annee || filters.dateDebut || filters.dateFin) && (
          <div className="flex justify-end mt-4">
            <button
              onClick={resetFilters}
              className="text-sm text-gray-600 hover:text-red-600 flex items-center gap-1"
            >
              <Icon name="close" size={16} />
              Réinitialiser tous les filtres
            </button>
          </div>
        )}
      </div>

      {/* Indication de la période */}
      {(filters.dateDebut || filters.dateFin) && (
        <div className="mb-4 text-sm text-gray-600 bg-blue-50 p-2 rounded-lg">
          📅 Période filtrée : du {filters.dateDebut ? new Date(filters.dateDebut).toLocaleDateString('fr-FR') : '...'}
          au {filters.dateFin ? new Date(filters.dateFin).toLocaleDateString('fr-FR') : '...'}
        </div>
      )}

      {/* Tableau par Type */}
      <div className="section-card bg-white rounded-xl p-6 mb-6">
        <h3 className="font-bold mb-4">📊 Par Type de Formation</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-center">Total</th>
                <th className="px-4 py-3 text-center">H</th>
                <th className="px-4 py-3 text-center">F</th>
                <th className="px-4 py-3 text-center">%F</th>
                <th className="px-4 py-3 text-center">Insérés</th>
                <th className="px-4 py-3 text-center">Taux</th>
                <th className="px-4 py-3 text-center">Sortie+</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {Object.keys(byTypeFiltered).map(type => {
                const data = byTypeFiltered[type];
                return (
                  <tr key={type} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{type}</td>
                    <td className="px-4 py-3 text-center">{data.total || 0}</td>
                    <td className="px-4 py-3 text-center">{data.hommes || 0}</td>
                    <td className="px-4 py-3 text-center">{data.femmes || 0}</td>
                    <td className="px-4 py-3 text-center">{data.tauxFemmes || 0}%</td>
                    <td className="px-4 py-3 text-center">{data.inseres || 0}</td>
                    <td className="px-4 py-3 text-center">{data.tauxInsertion || 0}%</td>
                    <td className="px-4 py-3 text-center">{data.tauxPositif || 0}%</td>
                  </tr>
                );
              })}
              {Object.keys(byTypeFiltered).length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Aucune donnée pour cette période
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4 flex items-center text-gray-800">
            <Icon name="book" size={20} className="mr-2 text-red-600" />
            Diplômes
          </h3>
          {getDiplomeData().length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={getDiplomeData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getDiplomeData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4 flex items-center text-gray-800">
            <Icon name="document" size={20} className="mr-2 text-red-600" />
            Statut Entrée
          </h3>
          {getStatutEntreeData().length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={getStatutEntreeData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4">👥 Genre</h3>
          {getGenreData().length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={getGenreData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getGenreData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4">📊 Sortie</h3>
          {getSortieData().length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={getSortieData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistiques;